/**
 * Hook für Push-Benachrichtigungen
 *
 * Registriert den Service Worker, fragt Berechtigung ab,
 * und abonniert Push-Benachrichtigungen via VAPID.
 */
import { useState, useEffect, useCallback } from "react";
import { trpc } from "@/lib/trpc";

import { createLogger } from "@/lib/logger";
const log = createLogger("Usepushnotifications");

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(clientId: string | undefined) {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [permission, setPermission] =
    useState<NotificationPermission>("default");

  const { data: vapidData } = trpc.push.getVapidKey.useQuery(undefined, {
    enabled: !!clientId,
  });
  const subscribeMutation = trpc.push.subscribe.useMutation();
  const unsubscribeMutation = trpc.push.unsubscribe.useMutation();

  // Check support and current state
  useEffect(() => {
    const supported =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);

      // Check if already subscribed
      navigator.serviceWorker.ready.then(registration => {
        registration.pushManager.getSubscription().then(sub => {
          setIsSubscribed(!!sub);
        });
      });
    }
  }, []);

  const subscribe = useCallback(async () => {
    if (!clientId || !vapidData?.publicKey || !isSupported) return;
    setIsLoading(true);

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setIsLoading(false);
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          vapidData.publicKey
        ) as unknown as BufferSource,
      });

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        throw new Error("Ungültige Subscription-Daten");
      }

      // Save to server
      await subscribeMutation.mutateAsync({
        clientId,
        endpoint: json.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      });

      setIsSubscribed(true);
    } catch (err) {
      log.error("Subscribe error:");
    } finally {
      setIsLoading(false);
    }
  }, [clientId, vapidData, isSupported, subscribeMutation]);

  const unsubscribe = useCallback(async () => {
    if (!clientId || !isSupported) return;
    setIsLoading(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await unsubscribeMutation.mutateAsync({
          clientId,
          endpoint: subscription.endpoint,
        });
      }

      setIsSubscribed(false);
    } catch (err) {
      log.error("Unsubscribe error:");
    } finally {
      setIsLoading(false);
    }
  }, [clientId, isSupported, unsubscribeMutation]);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    subscribe,
    unsubscribe,
  };
}
